import { HttpError } from '@littlebird/auth-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import {
  hasConnectionCardCompletion,
  sendConnectionCardCompletion,
} from '@/features/chat/chat-send';
import { integrationKeys } from '@/features/integrations/integrations.query';
import type { ConnectionCardInfo } from '@/features/integrations/integrations.types';
import { mcpKeys } from '@/features/mcp/mcp.query';
import { MCPAddServerModal } from '@/features/mcp/MCPAddServerModal';
import { MCPIcon } from '@/features/mcp/MCPIcon';
import { IntegrationConnectionModal } from '@/integrations';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { isPartialConnectionFlowRef } from '@/utils/oauth-url-parser';
import { handleExternalUrlClick } from '@/utils/platform';

export interface OAuthConnectionCardProps {
  threadId: string;
  flowRef?: string;
  redirectUrl?: string;
  toolkitName?: string;
  provider?: 'composio' | 'mcp';
  displayName?: string;
  accountLabel?: string;
  accountId?: string;
  serverId?: string;
  flowId?: string;
}

interface ConnectionDetails {
  provider: 'composio' | 'mcp';
  toolkitName: string;
  displayName?: string;
  description?: string;
  action: ConnectionAction;
  state: 'pending' | 'disabled' | 'connected' | 'unavailable';
  redirectUrl?: string;
  accountLabel?: string;
  accountId?: string;
  serverId?: string;
  flowId?: string;
}

type ConnectionAction = 'connect' | 'reconnect' | 'enable';

interface SettledCardAction {
  action: ConnectionAction;
  cardKey: string;
  continuationCompleted: boolean;
}

const connectionCardKey = (flowRef: string) =>
  ['integration-connection-card', flowRef] as const;

const isRetryableConnectionCardError = (error: unknown) =>
  !(error instanceof HttpError) ||
  error.status >= 500 ||
  error.status === 408 ||
  error.status === 429;

const CONNECTION_CARD_STALE_MS = 30_000;
const CONNECTION_CARD_CACHE_MS = 5 * 60_000;

/** Resolve a signed connection reference, with legacy link fields as a fallback. */
function useConnectionDetails({
  flowRef,
  redirectUrl,
  toolkitName,
  provider = 'composio',
  displayName,
  accountLabel,
  accountId,
  serverId,
  flowId,
}: OAuthConnectionCardProps): {
  details: ConnectionDetails | null;
  isLoading: boolean;
  failed: boolean;
  canRetry: boolean;
  retry: () => void;
} {
  const connectionCardQuery = useQuery<ConnectionCardInfo>({
    queryKey: connectionCardKey(flowRef ?? ''),
    queryFn: () => {
      if (!flowRef) throw new Error('Connection card reference is required');
      return backendApis.integrations.resolveConnectionCard(flowRef);
    },
    // While the message streams, the ref arrives in chunks; fetching those
    // partial refs would fire a doomed request per chunk.
    enabled: Boolean(flowRef && !isPartialConnectionFlowRef(flowRef)),
    retry: (failureCount, error) =>
      isRetryableConnectionCardError(error) && failureCount < 2,
    retryDelay: 250,
    staleTime: CONNECTION_CARD_STALE_MS,
    gcTime: CONNECTION_CARD_CACHE_MS,
  });

  if (flowRef) {
    const info = connectionCardQuery.data;
    return {
      details: info
        ? {
            provider: info.provider,
            toolkitName: info.integration_id.replace(/^mcp:/, ''),
            displayName: info.display_name,
            description: info.description,
            action: info.action ?? 'connect',
            state: info.state,
            redirectUrl: info.redirect_url ?? undefined,
            accountId: info.account_id || undefined,
            serverId: info.server_id || undefined,
            flowId: info.flow_id || undefined,
          }
        : null,
      isLoading:
        connectionCardQuery.isPending || connectionCardQuery.isFetching,
      failed: connectionCardQuery.isError,
      canRetry:
        connectionCardQuery.isError &&
        isRetryableConnectionCardError(connectionCardQuery.error),
      retry: () => void connectionCardQuery.refetch(),
    };
  }

  if (!redirectUrl || !toolkitName) {
    return {
      details: null,
      isLoading: false,
      failed: true,
      canRetry: false,
      retry: () => undefined,
    };
  }
  return {
    details: {
      provider,
      toolkitName,
      displayName,
      action: 'connect',
      state: 'pending',
      redirectUrl,
      accountLabel,
      accountId,
      serverId,
      flowId,
    },
    isLoading: false,
    failed: false,
    canRetry: false,
    retry: () => undefined,
  };
}

export const OAuthConnectionCard = observer(function OAuthConnectionCard(
  props: OAuthConnectionCardProps
) {
  const queryClient = useQueryClient();
  const [isActing, setIsActing] = useState(false);
  const [settledCard, setSettledCard] = useState<SettledCardAction | null>(
    null
  );
  const { details, isLoading, failed, canRetry, retry } =
    useConnectionDetails(props);
  const cardKey = props.flowRef ?? props.redirectUrl ?? '';

  const provider = details?.provider ?? 'composio';
  const toolkitName = details?.toolkitName ?? 'unknown';
  const integrationDisplayName =
    details?.displayName ||
    (toolkitName === 'unknown'
      ? 'Integration'
      : toolkitName.charAt(0).toUpperCase() +
        toolkitName.slice(1).replace(/[-_]/g, ' '));
  const IntegrationIcon = getIntegrationIcon(
    toolkitName === 'unknown' ? integrationDisplayName : toolkitName
  );
  const localSettledCard =
    settledCard?.cardKey === cardKey ? settledCard : null;
  const continuationAlreadySent =
    cardKey.length > 0 && hasConnectionCardCompletion(props.threadId, cardKey);

  /** Resume this thread after its account action completes successfully. */
  const continueConversation = useCallback(
    async (action: ConnectionAction): Promise<boolean> => {
      try {
        await sendConnectionCardCompletion({
          action: action === 'enable' ? 'enable' : 'connect',
          cardKey,
          displayName: integrationDisplayName,
          threadId: props.threadId,
        });
        return true;
      } catch (error) {
        console.error(
          'Failed to continue chat after connection action:',
          error
        );
        toast.error(
          `${integrationDisplayName} was updated, but the chat could not continue automatically`
        );
        return false;
      }
    },
    [cardKey, integrationDisplayName, props.threadId]
  );

  /** Record a successful account action and keep continuation retryable until its chat send succeeds. */
  const completeAction = useCallback(
    async (action: ConnectionAction) => {
      setSettledCard({
        action,
        cardKey,
        continuationCompleted: false,
      });
      setIsActing(true);
      try {
        if (await continueConversation(action)) {
          setSettledCard({
            action,
            cardKey,
            continuationCompleted: true,
          });
        }
      } finally {
        setIsActing(false);
      }
    },
    [cardKey, continueConversation]
  );

  const refreshSignedCard = useCallback(() => {
    if (!props.flowRef) return;
    void queryClient.invalidateQueries({
      queryKey: connectionCardKey(props.flowRef),
    });
  }, [props.flowRef, queryClient]);

  const handleAction = useCallback(async () => {
    if (isActing || !details) return;

    if (details.action === 'enable' && details.state === 'disabled') {
      setIsActing(true);
      try {
        if (details.provider === 'composio' && details.accountId) {
          await backendApis.integrations.toggleIntegrationById(
            details.accountId,
            true
          );
          void queryClient.invalidateQueries({ queryKey: integrationKeys.all });
        } else if (details.provider === 'mcp' && details.serverId) {
          await backendApis.mcp.setServerEnabled(details.serverId, true);
          void queryClient.invalidateQueries({ queryKey: mcpKeys.all });
        } else {
          toast.error(`Failed to enable ${integrationDisplayName}`);
          return;
        }
        await completeAction('enable');
      } catch (error) {
        console.error('Failed to enable integration:', error);
        toast.error(`Failed to enable ${integrationDisplayName}`);
      } finally {
        setIsActing(false);
      }
      return;
    }

    if (details.state !== 'pending' || !details.redirectUrl) return;

    if (details.provider === 'mcp') {
      if (!details.serverId || !details.flowId) {
        handleExternalUrlClick(details.redirectUrl, { forceInBrowser: true });
        return;
      }
      if (ModalsManager.isOpen(MCPAddServerModal)) return;

      setIsActing(true);
      const opened = ModalsManager.show(
        MCPAddServerModal,
        {
          reconnectServerId: details.serverId,
          reconnectServerName: integrationDisplayName,
          reconnectFlowId: details.flowId,
          onOAuthSettled: (success: boolean) => {
            setIsActing(false);
            refreshSignedCard();
            if (success) void completeAction(details.action);
          },
        },
        { unique: true }
      );
      if (!opened) {
        setIsActing(false);
        refreshSignedCard();
        return;
      }
      handleExternalUrlClick(details.redirectUrl, { forceInBrowser: true });
      return;
    }

    if (ModalsManager.isOpen(IntegrationConnectionModal)) return;

    setIsActing(true);

    let resolvedAccountId = details.accountId ?? null;
    if (!resolvedAccountId) {
      try {
        const accounts = await Promise.race([
          backendApis.integrations.getConnectedAccounts(details.toolkitName),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), 3000)
          ),
        ]);
        resolvedAccountId =
          [...accounts].reverse().find((account) => {
            return (
              account.connection_status === 'pending' &&
              account.account_label === details.accountLabel
            );
          })?.id ?? null;
      } catch {
        // Non-fatal — modal polling will handle resolution.
      }
    }

    try {
      const opened = ModalsManager.show(
        IntegrationConnectionModal,
        {
          redirectUrl: details.redirectUrl,
          toolkitName: details.toolkitName,
          displayName: integrationDisplayName,
          accountLabel: details.accountLabel,
          accountId: resolvedAccountId,
          flowId: details.flowId ?? null,
          onComplete: (success: boolean) => {
            setIsActing(false);
            refreshSignedCard();
            if (success) {
              void completeAction(details.action);
            } else {
              toast.error(`Failed to connect ${integrationDisplayName}`);
            }
          },
        },
        { unique: true }
      );
      if (!opened) setIsActing(false);
    } catch (error) {
      console.error('Failed to initiate OAuth connection:', error);
      toast.error('Failed to start connection process');
      setIsActing(false);
    }
  }, [
    completeAction,
    details,
    integrationDisplayName,
    isActing,
    queryClient,
    refreshSignedCard,
  ]);

  const isConnected =
    details?.state === 'connected' || localSettledCard !== null;
  const continuationAction =
    localSettledCard?.action ??
    (details?.state === 'connected' ? details.action : null);
  const needsContinuation =
    isConnected &&
    continuationAction !== null &&
    !localSettledCard?.continuationCompleted &&
    !continuationAlreadySent;
  const isDisabled = details?.state === 'disabled' && localSettledCard === null;
  const isUnavailable =
    failed ||
    (!isLoading && (details === null || details.state === 'unavailable'));
  const actionLabel = isDisabled
    ? 'Enable'
    : details?.action === 'reconnect'
      ? 'Reconnect'
      : 'Connect';
  const completedAction =
    localSettledCard?.action ??
    (details?.state === 'connected' ? details.action : null);
  const successLabel =
    completedAction === 'enable'
      ? 'Enabled successfully'
      : 'Connected successfully';
  const loadingActionLabel = isLoading
    ? 'Preparing'
    : isDisabled
      ? 'Enabling'
      : details?.action === 'reconnect'
        ? 'Reconnecting'
        : 'Connecting';
  const cardTitle = isUnavailable
    ? 'Connection unavailable'
    : integrationDisplayName;
  const cardDescription = isLoading
    ? 'Checking authorization details'
    : isUnavailable
      ? 'Ask to start a new connection'
      : details?.description ||
        (isConnected
          ? `${integrationDisplayName} is ready to use`
          : `${actionLabel} ${integrationDisplayName} to Littlebird`);

  return (
    <span className="block w-full max-w-2xl my-3">
      <span className="flex items-center gap-4 w-full rounded-xl border border-border dark:border-border-strong bg-surface px-4 py-0">
        {provider === 'mcp' ? (
          <MCPIcon
            server={{ name: integrationDisplayName }}
            className="size-6 shrink-0"
          />
        ) : (
          <IntegrationIcon className="size-6 shrink-0" />
        )}

        <span className="flex-1 flex flex-col text-left justify-center min-w-0 gap-1">
          <span className="block text-base font-medium text-foreground truncate">
            {cardTitle}
          </span>
          <span className="block text-sm text-foreground-muted truncate">
            {cardDescription}
          </span>
        </span>

        {isConnected && needsContinuation ? (
          <Button
            variant="secondary"
            size="sm"
            textSize="base"
            onClick={() => void completeAction(continuationAction)}
            isLoading={isActing}
            loadingText="Continuing"
            aria-label={`Continue with ${integrationDisplayName}`}
          >
            Continue
          </Button>
        ) : isConnected ? (
          <span
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-success-3 px-3 text-sm font-medium text-success-11"
            role="status"
            aria-live="polite"
          >
            <Icons.check className="size-4 shrink-0" aria-hidden="true" />
            {successLabel}
          </span>
        ) : isUnavailable ? (
          canRetry ? (
            <Button
              variant="secondary"
              size="sm"
              textSize="base"
              onClick={retry}
              isLoading={isLoading}
              loadingText="Retrying"
              aria-label={`Retry ${integrationDisplayName}`}
            >
              Retry
            </Button>
          ) : null
        ) : (
          <Button
            variant="secondary"
            size="sm"
            textSize="base"
            onClick={handleAction}
            isLoading={isLoading || isActing}
            loadingText={loadingActionLabel}
            aria-label={`${actionLabel} ${integrationDisplayName}`}
          >
            {actionLabel}
          </Button>
        )}
      </span>
    </span>
  );
});
